import { Fragment, useState } from "react";

import { Classes, Colors } from "@blueprintjs/core";
import orderBy from "lodash/orderBy";

import { useTableSort } from "@/components/helpers/custom-hooks/use-table-sort";
import { Box, Flex } from "@/components/layout/flexbox";
import { ProgramDashboardDialogs } from "@/components/pages_logged_in/program/components/program-dashboard/ProgramDashboardDialogs";
import {
  TOC,
  TOCBox,
} from "@/components/pages_logged_in/workspace-admin/WorkspaceMemberTable";
import { Header } from "@/components/pieces/table/Header";
import { ComponentStatus, ComponentTypeEnum } from "@/graphql";
import {
  ComponentDialog,
  isLeadComponent,
  LeadComponent,
  ParticipantComponent,
} from "@/types/programs";
import { HeaderConfig } from "@/types/tables";

import { ComponentTableColumns } from "./ComponentTableColumns";

export const isOneOneCoachingChildren = (type: ComponentTypeEnum) =>
  [
    ComponentTypeEnum.OneOneCoachingIntro,
    ComponentTypeEnum.OneOneCoachingSession,
  ].includes(type);

type ProgramComponentTableField =
  | "name"
  | "actions"
  | "targetStartDate"
  | "targetDueDate"
  | "targetEndDate";

interface ProgramHeaderConfig extends HeaderConfig {
  field: ProgramComponentTableField;
}

const DEFAULT_HEADERS: ProgramHeaderConfig[] = [
  {
    field: "name",
    title: "Component",
    justifyContent: "flex-start",
    widthClass: "w-1/3",
  },
  {
    field: "targetStartDate",
    title: "Launch Date",
    justifyContent: "flex-start",
    widthClass: "w-60",
  },
  {
    field: "targetDueDate",
    title: "Due Date",
    justifyContent: "flex-start",
    widthClass: "w-60",
  },
];

const LEAD_HEADERS: ProgramHeaderConfig[] = [
  ...DEFAULT_HEADERS,
  {
    field: "targetEndDate",
    title: "Closed Date",
    justifyContent: "flex-start",
    widthClass: "w-60",
  },
  {
    field: "actions",
    title: "Actions",
    disableSorting: true,
    widthClass: "w-16",
    justifyContent: "flex-end",
  },
];

type ProgramComponent = (LeadComponent | ParticipantComponent) & {
  program?: LeadComponent["program"];
  parentComponent?: ParticipantComponent["parentComponent"];
};

type ParentComponent = NonNullable<ParticipantComponent["parentComponent"]>;
type SelectedComponent = (ProgramComponent | ParentComponent) & {
  type?: ComponentTypeEnum;
};

interface ProgramComponentTableProps {
  components: ProgramComponent[];
  isLead: boolean;
}

export function ProgramComponentTable({
  components,
  isLead,
}: ProgramComponentTableProps) {
  const headers: ProgramHeaderConfig[] = isLead
    ? LEAD_HEADERS
    : DEFAULT_HEADERS;

  const activeComponents = components.filter((component) => {
    return ![ComponentStatus.InDesign].includes(component.status);
  });

  const [activeDialog, setActiveDialog] = useState<ComponentDialog>();
  const [selectedComponent, setSelectedComponent] =
    useState<SelectedComponent>();

  const closeDialog = () => {
    setActiveDialog(undefined);
    setSelectedComponent(undefined);
  };

  function handleEditComponent(component: ProgramComponent) {
    if (
      [
        ComponentTypeEnum.OneOneCoachingIntro,
        ComponentTypeEnum.OneOneCoachingSession,
      ].includes(component.type) &&
      component.parentComponent
    ) {
      setSelectedComponent(component.parentComponent);
    } else {
      setSelectedComponent(component);
    }
    setActiveDialog("add-component");
  }

  function handleDeleteComponent(component: ProgramComponent) {
    setSelectedComponent(component);
    setActiveDialog("delete-component");
  }

  function handleCloseComponent(component: ProgramComponent) {
    setSelectedComponent(component);
    setActiveDialog("close-component");
  }

  const { currentSort, updateSortState } =
    useTableSort<ProgramComponentTableField>("programComponentListSort");

  const sortedComponents = currentSort.field
    ? orderBy(
        activeComponents,
        [currentSort.field],
        [currentSort.asc ? "asc" : "desc"],
      )
    : activeComponents;

  const [expandedRows, setExpandedRows] = useState<string[]>([]);

  return (
    <>
      {selectedComponent &&
        isLeadComponent(selectedComponent) &&
        selectedComponent.program && (
          <ProgramDashboardDialogs
            program={selectedComponent.program}
            selectedComponent={selectedComponent}
            dialog={activeDialog}
            onCloseDialog={closeDialog}
          />
        )}

      <Box
        style={{
          border: "1px solid #E7E7ED",
          borderRadius: 8,
          padding: "0 3px",
        }}
        mt={36}
      >
        <TOCBox width="100%" height="100%">
          <TOC className={`${Classes.HTML_TABLE} noboder-table border-none`}>
            <thead>
              <tr>
                {headers.map((config) => (
                  <Header
                    key={config?.field}
                    config={config}
                    currentSort={currentSort}
                    onSort={updateSortState}
                  />
                ))}
              </tr>
            </thead>

            <tbody>
              {sortedComponents.map((row) => {
                const rowKey = `program-item-${row?.id}`;
                const isExpanded = expandedRows.includes(rowKey);

                return (
                  <Fragment key={rowKey}>
                    <tr>
                      <ComponentTableColumns
                        displayColumns={headers}
                        row={row}
                        onDelete={() => handleDeleteComponent(row)}
                        onEdit={() => handleEditComponent(row)}
                        onClose={() => handleCloseComponent(row)}
                        isLead={isLead}
                        isExpanded={isExpanded}
                        onExpand={(value) => {
                          setExpandedRows(
                            value
                              ? [...expandedRows, rowKey]
                              : expandedRows.filter((key) => key !== rowKey),
                          );
                        }}
                      />
                    </tr>
                    {isExpanded && row.childComponents?.length
                      ? row.childComponents.map((child) => (
                          <tr key={`program-item-${child?.id}`}>
                            <ComponentTableColumns
                              displayColumns={headers}
                              row={child as ProgramComponent}
                              onDelete={() =>
                                handleDeleteComponent(child as ProgramComponent)
                              }
                              onEdit={() =>
                                handleEditComponent(child as ProgramComponent)
                              }
                              onClose={() =>
                                handleCloseComponent(child as ProgramComponent)
                              }
                              isLead={isLead}
                              isChild={true}
                            />
                          </tr>
                        ))
                      : null}
                  </Fragment>
                );
              })}
            </tbody>
          </TOC>
          {!activeComponents.length ? (
            <Flex
              css={{
                background: Colors.WHITE,
                borderBottom: `1px solid ${Colors.LIGHT_GRAY2}`,
                minHeight: 200,
              }}
              width="100%"
              py="24px"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
            >
              <Box my={2}>No Components Yet</Box>
            </Flex>
          ) : null}
        </TOCBox>
      </Box>
    </>
  );
}
